import React, { FC } from 'react';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { EditPencilIcon, CirclePlusIcon } from 'components/Elements/CustomSVGIcon';
import { Incident, DiaryEntry } from 'core/incidents/types';
import DiaryEntryCard from '../DiaryEntryCard';
import IncidentImages from '../IncidentImages';
import './incidentExpandedRow.scss';

interface IncidentExpandedRowProps {
    record: Incident;
    // eslint-disable-next-line react/require-default-props
    onEditClick?: () => void;
    // eslint-disable-next-line react/require-default-props
    onMarkCompleteClick?: () => void;
    // eslint-disable-next-line react/require-default-props
    onReopenClick?: () => void;
    // eslint-disable-next-line react/require-default-props
    onViewSiteDetails?: () => void;
    // eslint-disable-next-line react/require-default-props
    onAddDiaryEntry?: () => void;
    // eslint-disable-next-line react/require-default-props
    onEditDiaryEntry?: (entry: DiaryEntry) => void;
    // Admin-level capability: diary create/edit endpoints are admin-only, so the
    // UI must not advertise them to non-admin (e.g. security) viewers.
    // eslint-disable-next-line react/require-default-props
    canManageDiaryEntries?: boolean;
}

const IncidentExpandedRow: FC<IncidentExpandedRowProps> = ({ record, onEditClick, onMarkCompleteClick, onReopenClick, onViewSiteDetails, onAddDiaryEntry, onEditDiaryEntry, canManageDiaryEntries = false }) => {
    const isClosed = !!record.completedAt;

    const referenceFields: { label: string; value: string }[] = [];
    if (record.khcReference) referenceFields.push({ label: 'KHC Reference:', value: record.khcReference });
    const remedyRef = record.remedyReference && typeof record.remedyReference === 'object'
        ? (record.remedyReference as unknown as { reference: string }).reference
        : record.remedyReference;
    if (remedyRef) referenceFields.push({ label: 'Remedy Reference:', value: remedyRef });
    if (record.maximoReference) referenceFields.push({ label: 'Maximo Reference:', value: record.maximoReference });
    if (record.conceptReference) referenceFields.push({ label: 'Concept Reference:', value: record.conceptReference });

    const topRowRef = referenceFields[0] || null;
    const remainingRefs = referenceFields.slice(1);

    return (
        <div className="incident-expanded-content">
            <div className="expanded-columns">
                <div className="expanded-left">
                    <div className="expanded-top-row">
                        {topRowRef ? (
                            <div className="meta-left">
                                <span className="meta-label">{topRowRef.label}</span>
                                <span className="meta-value">{topRowRef.value}</span>
                            </div>
                        ) : (
                            <div className="meta-left" />
                        )}
                        {!isClosed && (
                            <button type="button" className="edit-incident-link" onClick={onEditClick}>
                                <EditPencilIcon />
                                Edit Incident
                            </button>
                        )}
                    </div>
                    {remainingRefs.map((ref) => (
                        <div className="meta-row" key={ref.label}>
                            <span className="meta-label">{ref.label}</span>
                            <span className="meta-value">{ref.value}</span>
                        </div>
                    ))}
                    {record.mitieDutyManagerInformedLabel && (
                        <div className="meta-row">
                            <span className="meta-label">Mitie Duty Manager Informed:</span>
                            <span className="meta-value">{record.mitieDutyManagerInformedLabel}</span>
                        </div>
                    )}
                    {record.policeAssigned !== null && (
                        <div className="meta-row">
                            <span className="meta-label">Police Assigned:</span>
                            <span className="meta-value">{record.policeAssigned ? 'Yes' : 'No'}</span>
                        </div>
                    )}
                    {record.policeAssigned ? (
                        record.policeReference && (
                            <div className="incident-description">
                                <h4>Police Reference</h4>
                                <p>{record.policeReference}</p>
                            </div>
                        )
                    ) : (
                        record.reason && (
                            <div className="incident-description">
                                <h4>Reason</h4>
                                <p>{record.reason}</p>
                            </div>
                        )
                    )}
                    {record.checkedInAt && (
                        <div className="meta-row">
                            <span className="meta-label">Check-in:</span>
                            <span className="meta-value">{dayjs(record.checkedInAt).format('DD/MM/YYYY HH:mm')}</span>
                        </div>
                    )}
                    {record.checkedOutAt && (
                        <div className="meta-row">
                            <span className="meta-label">Check-out:</span>
                            <span className="meta-value">{dayjs(record.checkedOutAt).format('DD/MM/YYYY HH:mm')}</span>
                        </div>
                    )}
                    <div className="incident-description">
                        <h4>Incident Description</h4>
                        <p>{record.description}</p>
                    </div>
                </div>
                <div className="expanded-right">
                    <div className="meta-right">
                        <div className="meta-item">
                            <span className="meta-right-label">Date Logged:</span>
                            <span className="meta-value">{record.dateLogged ? dayjs(record.dateLogged).format('DD/MM/YYYY') : '-'}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-right-label">Logged By:</span>
                            <span className="meta-value">{record.loggedByUser?.name ?? '-'}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-right-label">Time Logged:</span>
                            <span className="meta-value">{dayjs(record.dateLogged).format('HH:mm')}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-right-label">Last Updated:</span>
                            <span className="meta-value">{dayjs(record.updatedAt).format('DD/MM/YYYY')}</span>
                        </div>
                    </div>
                    {onViewSiteDetails && record.site?.encryptedId && (
                        <Button className="view-site-details-btn" onClick={onViewSiteDetails}>
                            View Site Details
                        </Button>
                    )}
                    {!isClosed && (
                        <Button type="primary" className="mark-complete-btn" onClick={onMarkCompleteClick}>
                            Mark as Complete
                        </Button>
                    )}
                </div>
            </div>

            <div className="diary-section">
                <div className="diary-columns">
                    <div className="diary-left">
                        <div className="diary-header">
                            <h4>Diary Entries</h4>
                            {!isClosed && canManageDiaryEntries && (
                                <button type="button" className="add-diary-link" onClick={onAddDiaryEntry}>
                                    <CirclePlusIcon />
                                    Add a diary entry
                                </button>
                            )}
                        </div>
                        {(record.diaryEntries || []).length > 0
                            ? (record.diaryEntries || []).map((entry) => (
                                <DiaryEntryCard
                                    key={entry.id}
                                    entry={entry}
                                    onEdit={!isClosed && canManageDiaryEntries && onEditDiaryEntry ? onEditDiaryEntry : undefined} />
                            ))
                            : <p className="no-diary-entries">No diary entries yet.</p>}
                    </div>
                    <div className="diary-right" />
                </div>
            </div>

            <div className="images-section">
                <IncidentImages
                    incidentId={record.id}
                    images={Array.isArray(record.images) ? record.images : []}
                    isClosed={isClosed} />
            </div>

            {isClosed && (
                <div className="resolution-section">
                    <div className="resolution-left">
                        <h4>Root Cause</h4>
                        <p>{record.rootCause || '-'}</p>
                        <h4>Resolution Details</h4>
                        <p>{record.resolutionDetails || '-'}</p>
                    </div>
                    <div className="resolution-right">
                        <div className="meta-right">
                            <div className="meta-item">
                                <span className="meta-right-label">Completed By:</span>
                                <span className="meta-value">{record.completedByUser?.name ?? '-'}</span>
                            </div>
                            <div className="meta-item">
                                <span className="meta-right-label">Date:</span>
                                <span className="meta-value">{dayjs(record.completedAt).format('DD/MM/YYYY')}</span>
                            </div>
                        </div>
                        {onReopenClick && (
                            <Button type="primary" className="reopen-incident-btn" onClick={onReopenClick}>
                                Reopen Incident
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncidentExpandedRow;
